import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import customAxios from "lib/axios";
import useAppDispatch from "hooks/useAppDispatch";
import useAppSelector from "hooks/useAppSelector";
import { show } from "store/reducers/notifications.reducer";
import {
  changeSaveStatus,
  changeValidationStatus,
  loadRobot,
  reset,
} from "store/reducers/robot.reducer";

/**
 * Hook used to interact with robot states
 * for loading, saving, deleting, etc.
 */
function useRobot() {
  const didMount = useRef(false);
  const dispatch = useAppDispatch();
  const robot = useAppSelector((state) => state.robot);
  const builder = useAppSelector((state) => state.builder);
  const userId = localStorage.getItem("user_id");
  const [t] = useTranslation(["errors"]);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    // TODO Update session every 5 seconds.
  }, []);

  /**
   * Effect used to change validation
   * validation & save status if builder changes.
   */
  useEffect(() => {
    // Evade execution on first mount.
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    dispatch(changeValidationStatus({ isValidated: false }));
    dispatch(changeSaveStatus({ saveStatus: false }));
  }, [builder.present, dispatch]);

  /**
   * Function used to save a robot into the Database
   */
  const save = async () => {
    try {
      const res = await customAxios.put("robots", {
        id: robot.id,
        title: robot.title,
        user: userId,
        startDate: format(robot.startDate, "yyyy-MM-dd HH:mm:ss"),
        endDate: format(robot.endDate, "yyyy-MM-dd HH:mm:ss"),
        balance: robot.balance,
        currency: robot.currency || "EUR",
        gain: robot.gain,
        profit: (robot.gain / robot.balance) * 100,
        risk: robot.relativeDrawdown,
        score: ((robot.gain / robot.balance) * 100) / robot.relativeDrawdown,
        trades_win: robot.tradesWon,
        trades_loss: robot.tradesLost,
        success_rate: robot.successRate,
        gains_only: robot.gainsOnly,
        loses_only: robot.losesOnly,
        best_gain: robot.bestGain,
        worst_loss: robot.worstLoss,
        gain_loss_rate: robot.gainLossRate,
        drawdown: robot.drawdown,
        relativeDrawdown: robot.relativeDrawdown,
        dailyDrawdown: robot.dailyDrawdown,
        runup: robot.runup,
        relativeRunup: robot.relativeRunup,
        dailyRunup: robot.dailyRunup,
        validated: robot.isValidated,
        image: robot.image,
      });
      dispatch(changeSaveStatus({ id: res.data.id, saveStatus: true }));
      dispatch(show({ type: "success", msg: t("builder:savedSuccesfully") })); // Pending transalation
      setWorking(false);
    } catch (error) {
      setWorking(false);
      throw new Error("Error saving robot. " + error);
    }
  };

  /**
   * Function used to load a robot into the App
   * @param id robot DB id
   */
  const load = async (id: number, shared: boolean) => {
    setWorking(true);
    try {
      const res = await customAxios.get(`robots/${id}`);
      dispatch(reset());
      dispatch(
        loadRobot({
          id: res.data.id, // TODO Don't return ID from API if is Shared Robot -- Why?? JuanM
          title: res.data.title,
          balance: res.data.balance,
          startDate: res.data.startDate
            ? res.data.startDate
            : new Date("2021-01-01"),
          isShared: shared,
          isValidated: res.data.isValidated > 0,
          // validation: res.data.validation, // Is this necessary? Find where it comes from
        })
      );
    } catch (e) {
      console.error(e);
      setWorking(false);
      throw new Error("Error loading robot.");
    }
  };

  return { save, load, working };
}

export default useRobot;
